import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type MultiSelectOption = { value: string; label: string };

type MultiSelectPopoverProps = {
  /** Accessible-name prefix for the trigger, e.g. "Platforms". */
  label: string;
  /** Shown when nothing is selected; also the "clear" action. */
  allLabel: string;
  /** Pre-translated summary shown when two or more options are selected. */
  countLabel: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
  icon?: ReactNode;
};

/** Checkbox list in a popover. Always emits values in `options` order so URLs stay stable. */
export const MultiSelectPopover = ({ label, allLabel, countLabel, options, value, onChange, idPrefix, icon }: MultiSelectPopoverProps) => {
  const selected = options.filter((option) => value.includes(option.value));
  const summary = selected.length === 0 ? allLabel : selected.length === 1 ? selected[0].label : countLabel;
  const toggle = (optionValue: string, checked: boolean) => {
    const next = checked ? [...value, optionValue] : value.filter((item) => item !== optionValue);
    onChange(options.map((option) => option.value).filter((item) => next.includes(item)));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-9 bg-background">
          {icon}
          <span className="sr-only">{label}: </span>
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <ul className="space-y-1">
          {options.map((option) => {
            const id = `${idPrefix}-${option.value}`;
            return (
              <li key={option.value} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted">
                <Checkbox id={id} checked={value.includes(option.value)} onCheckedChange={(checked) => toggle(option.value, checked === true)} />
                <label htmlFor={id} className="flex-1 cursor-pointer text-sm">{option.label}</label>
              </li>
            );
          })}
        </ul>
        {value.length > 0 && (
          <Button type="button" variant="ghost" size="sm" className="mt-1 w-full" onClick={() => onChange([])}>
            {allLabel}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
